import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import {
  DsHeaderModule,
  DsHeaderLabellingConfiguration,
  DsHeaderUserInfo,
  DsTagModule,
  DsListItemModule,
  DsModalModule,
  DsTreeModule,
  DsTreeItem,
  DsTreeItemChange,
  DsNavigationBarModule,
  DsNavigationItem,
  DsButtonModule,
  DsIconModule,
  DsToastService,
  DsToastModel,
} from '@bmw-ds/components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { DealerStore } from './core/stores/dealer.store';
import { UserStore } from './core/stores/user.store';
import { DisableImprintDirective } from './shared/directives/disable-imprint.directive';
import { LoadingDirective } from './shared/directives/loading.directive';
import { TagTone } from './shared/enums/tone.enum';
import { LanguageSettingsModalComponent } from './views/modals/language-settings-modal/language-settings-modal.component';
import { ModalService } from './core/services/modal.service';
import { LocaleService } from './core/services/locale.service';
import { LANGUAGE_OPTIONS } from './shared/utils/language.utils';
import { LegalTermsIdEnum } from 'src/app/shared/enums/legal-terms.enum';
import { ValidPages } from './shared/enums/valid-pages.enum';
import { Subject } from 'rxjs/internal/Subject';
import { ImprintComponent } from './views/modals/imprint-modal/imprint.component';
import { LegalNoticeModalComponent } from './views/modals/legal-notice-modal/legal-notice-modal.component';
import { TermsAndConditionsModalComponent } from './views/modals/terms-and-conditions-modal/terms-and-conditions-modal.component';
import { DataPrivacyDisclaimersModalComponent } from './views/modals/data-privacy-disclaimers-modal/data-privacy-disclaimers-modal.component';

@Component({
  selector: 'pure-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [
    RouterOutlet,
    DsHeaderModule,
    LoadingDirective,
    DsTagModule,
    DsListItemModule,
    TranslateModule,
    DsModalModule,
    DsTreeModule,
    ImprintComponent,
    DsNavigationBarModule,
    DsIconModule,
    DsButtonModule,
    DisableImprintDirective,
  ],
})
export class AppComponent {
  protected readonly tagTone = TagTone;

  private readonly dealerStore = inject(DealerStore);
  private readonly userStore = inject(UserStore);
  protected readonly router = inject(Router);
  private readonly modalService = inject(ModalService);
  private readonly translateService = inject(TranslateService);
  private readonly toastService = inject(DsToastService);
  private readonly localeService = inject(LocaleService);

  private readonly purePage = 'PureDashboard';
  private readonly batteryCarePage = 'BatteryCare';
  private readonly qNumberTicketUrl = 'https://bmwgroupprod.service-now.com/mybmwit?id=bmw_ticket_self_service&type=it_interruption&short_description=PURE: General Problems with PURE Dashboard&kb_number=KB000000725567&routing_information=undefined';
  private readonly cNumberTicketUrl = 'https://ilz-user-prod-wen.bmwgroup.com/ispi-cockpit';
  private readonly qNumberCompassUrl = 'https://auth.bmwgroup.net/auth/XUI/?realm=/intranetb2x&spEntityID=https://compass-bmw.kcenter.usu.com&goto=https://auth.bmwgroup.net/auth/saml2/jsp/idpSSOInit.jsp?metaAlias%3D/intranetb2x/saml20-intranetb2x%26spEntityID%3Dhttps://compass-bmw.kcenter.usu.com%26NameIDFormat%3Durn:oasis:names:tc:SAML:1.1:nameid-format:unspecified%26RelayState%3Dhttps://compass-bmw.kcenter.usu.com/%26redirected%3Dtrue#login/';
  private readonly cNumberCompassUrl = 'https://auth.bmwgroup.com/auth/XUI/?realm=/internetb2x&spEntityID=https://compass-bmw.kcenter.usu.com&service=strongAuth4000Service&goto=https://auth.bmwgroup.com/auth/saml2/jsp/idpSSOInit.jsp?metaAlias=/internetb2x/saml20-internetb2x&spEntityID=https://compass-bmw.kcenter.usu.com%26NameIDFormat%3Durn:oasis:names:tc:SAML:1.1:nameid-format:unspecified%26RelayState%3Dhttps://compass-bmw.kcenter.usu.com/%26redirected%3Dtrue#login/';

  protected title = signal<string>('PURE');

  // BatteryCare - Signals to control the navigation bar state
  isExpanded = signal<boolean>(true);
  currentNavigationPage = signal<string>('');
  hasPureAccess = computed(() => this.userStore.checkUserHasAccess(ValidPages.APPLICATION));
  hasBatteryCareAccess = computed(() => this.userStore.checkUserHasAccess(ValidPages.BATTERY_CARE));
  showNavigationBar = computed(() => this.hasPureAccess() && this.hasBatteryCareAccess());
  navigationItems = computed(() => this.computeNavigationItems());

  protected readonly localeOptionsStatic = LANGUAGE_OPTIONS.map((lang) => ({
    id: lang.isoCountryCode,
    label: lang.isoCountryCode,
    language: lang.name,
  }));

  protected localeID = '';
  protected localeOptionActive = [{ id: 'en', label: 'en', language: 'English' }];
  protected legalNoticeModalOpen = false;
  protected termsAndConditionsModalOpen = false;
  protected dataPrivacyDisclaimersModalOpen = false;
  private readonly logoutLabel = toSignal(this.translateService.stream('common.buttons.logout'), {
    initialValue: this.translateService.instant('common.buttons.logout'),
  });

  updateLegalTerms$!: Subject<DsTreeItemChange[]>;

  legalTerms: DsTreeItem[] = [];

  protected userInformation = computed(() => {
    return {
      username: this.userStore.userInformation()?.name,
    } as DsHeaderUserInfo;
  });

  protected userPreferredLanguage = computed(() => this.userStore.userPreferredLanguage());

  protected loading = computed(() => this.userStore.isLoading() || this.dealerStore.isLoading());
  protected readonly headerLabellingConfig = computed<DsHeaderLabellingConfiguration>(() => ({
    logout: this.logoutLabel(),
  }));

  constructor() {
    effect(() => {
      const localStorageLang = localStorage.getItem('userPreferredLanguage');
      const userPreferredLanguage = this.userPreferredLanguage();

      if (localStorageLang) {
        this.changeLocale(localStorageLang);
      } else {
        if (userPreferredLanguage) {
          this.changeLocale(userPreferredLanguage);
        } else {
          this.setDefaultLanguage();
        }
      }
    });

    // BatteryCare - Keep the navigation bar selection in sync with the current route.
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const url = this.router.url;

      this.currentNavigationPage.set('');

      setTimeout(() => {
        if (url.startsWith('/dashboard') || url.startsWith('/stepper') || url.startsWith('/backoffice')) {
          this.currentNavigationPage.set(this.purePage);
        } else if (url.startsWith('/batterycare')) {
          this.currentNavigationPage.set(this.batteryCarePage);
        }
      });
    });
  }

  getUpdateLegalTermsSubject(subject: Subject<DsTreeItemChange[]>): void {
    this.updateLegalTerms$ = subject;
  }

  protected openLanguageSettingsModal(): void {
    this.modalService.openDialog(LanguageSettingsModalComponent);
  }

  protected openCompassLink(): void {
    const bmwUniqueUid = this.userStore.userInformation()?.bmwUniqueUid ?? '';
    if (bmwUniqueUid.toLowerCase().startsWith('q')) {
      window.open(this.qNumberCompassUrl, '_blank');
    } else if (bmwUniqueUid.toLowerCase().startsWith('c')) {
      window.open(this.cNumberCompassUrl, '_blank');
    }
  }

  protected openLegalTermsModal(legalTermId: string | string[] | null): void {
    if (this.legalNoticeModalOpen || this.termsAndConditionsModalOpen || this.dataPrivacyDisclaimersModalOpen) {
      return;
    }

    switch (legalTermId) {
      case LegalTermsIdEnum.LEGAL_NOTICE: {
        this.legalNoticeModalOpen = true;
        const dialogRef = this.modalService.openDialog(LegalNoticeModalComponent);
        dialogRef.closed.subscribe(() => {
          this.legalNoticeModalOpen = false;
          this.deSelectLegalTerm(LegalTermsIdEnum.LEGAL_NOTICE);
        });
        break;
      }
      case LegalTermsIdEnum.TERMS_AND_CONDITIONS: {
        this.termsAndConditionsModalOpen = true;
        const dialogRef = this.modalService.openDialog(TermsAndConditionsModalComponent);
        dialogRef.closed.subscribe(() => {
          this.termsAndConditionsModalOpen = false;
          this.deSelectLegalTerm(LegalTermsIdEnum.TERMS_AND_CONDITIONS);
        });
        break;
      }
      case LegalTermsIdEnum.DATA_PRIVACY_DISCLAIMERS: {
        this.dataPrivacyDisclaimersModalOpen = true;
        const dialogRef = this.modalService.openDialog(DataPrivacyDisclaimersModalComponent);
        dialogRef.closed.subscribe(() => {
          this.dataPrivacyDisclaimersModalOpen = false;
          this.deSelectLegalTerm(LegalTermsIdEnum.DATA_PRIVACY_DISCLAIMERS);
        });
        break;
      }
      default:
        break;
    }
  }

  deSelectLegalTerm(termToDeselect: LegalTermsIdEnum): void {
    const itemsToChange: DsTreeItemChange[] = [
      {
        item: {
          id: termToDeselect,
          isSelected: false,
        },
        action: 'edit',
      },
    ];
    this.updateLegalTerms$.next(itemsToChange);
  }

  protected setDefaultLanguage(): void {
    const userInfo = this.userStore.userInformation();
    if (userInfo) {
      const userDefaultLanguage = userInfo.userCountryCode.toLowerCase();
      this.setLocaleOptionActive(userDefaultLanguage);

      if (userDefaultLanguage) {
        this.changeLocale(this.localeOptionsStatic.find((option) => option.id === userDefaultLanguage)!.id);
      }
    }
  }

  protected changeLocale(localeID?: string | null): void {
    if (localeID) {
      this.localeID = localeID;
      this.localeService.setLanguage(localeID);
      this.userStore.setUserPreferredLanguage(localeID);
      this.setLocaleOptionActive(localeID);
      this.translateService.use(localeID).subscribe({
        next: () => {
          this.updateLegalTermsTranslations();
        },
        error: (err) => {
          if (err?.status === 404) {
            this.localeID = 'en';
            this.localeService.setLanguage(this.localeID);
            this.translateService.use(this.localeID).subscribe(() => {
              this.updateLegalTermsTranslations();
            });
          }
        },
      });
    } else {
      this.localeID = 'en';
      this.localeService.setLanguage(this.localeID);
      this.userStore.setUserPreferredLanguage(this.localeID);
      this.setLocaleOptionActive(this.localeID);
      this.translateService.use(this.localeID).subscribe(() => {
        this.updateLegalTermsTranslations();
      });
    }
  }

  private updateLegalTermsTranslations(): void {
    this.legalTerms = [
      {
        id: LegalTermsIdEnum.LEGAL_TERMS,
        label: this.translateService.instant('header.legalTerms'),
        children: [
          {
            id: LegalTermsIdEnum.LEGAL_NOTICE,
            label: this.translateService.instant('header.legalNoticeModal.title'),
          },
          {
            id: LegalTermsIdEnum.TERMS_AND_CONDITIONS,
            label: this.translateService.instant('header.termsAndConditionsModal.title'),
          },
          {
            id: LegalTermsIdEnum.DATA_PRIVACY_DISCLAIMERS,
            label: this.translateService.instant('header.dataPrivacyDisclaimersModal.title'),
          },
        ],
      },
    ];
  }

  protected setLocaleOptionActive(countryCode: string): void {
    this.localeOptionActive = [];
    const activeLocale = this.localeOptionsStatic.find((option) => option.id === countryCode);
    if (activeLocale) {
      this.localeOptionActive.push(activeLocale);
    }
  }

  protected logout(): void {
    this.userStore.logout();
  }

  protected openCreateTicket(): void {
    const bmwUniqueUid = this.userStore.userInformation()!.bmwUniqueUid;
    if (bmwUniqueUid) {
      const firstChar = bmwUniqueUid.charAt(0).toLowerCase();
      if (firstChar === 'q') {
        window.open(this.qNumberTicketUrl, '_blank');
      } else if (firstChar === 'c') {
        window.open(this.cNumberTicketUrl, '_blank');
      } else {
        this.toastService.pushToast({
          id: 'create-ticket-error-format',
          tone: 'critical',
          toastText: this.translateService.instant('dashboard.ticketBtn.unexpectedUidFormat'),
        } as DsToastModel);
      }
    } else {
      this.toastService.pushToast({
        id: 'create-ticket-error-not-available',
        tone: 'critical',
        toastText: this.translateService.instant('dashboard.ticketBtn.uidNotAvailable'),
      } as DsToastModel);
    }
  }

  // BatteryCare - Compute the navigation items to be shown on the ds-navigation-bar based on the user's roles
  private computeNavigationItems(): DsNavigationItem[] {
    const items: DsNavigationItem[] = [];

    if (this.hasPureAccess()) {
      items.push({
        id: this.purePage,
        label: this.translateService.instant('navBar.pureDashboard'),
        routerLink: '/dashboard/open',
        icon: 'car',
      } as DsNavigationItem);
    }

    if (this.hasBatteryCareAccess()) {
      items.push({
        id: this.batteryCarePage,
        label: this.translateService.instant('navBar.batteryCare'),
        routerLink: '/batterycare/dashboard',
        icon: 'flashlight',
      } as DsNavigationItem);
    }

    return [
      {
        id: 'Services',
        label: this.translateService.instant('navBar.services'),
        group: true,
        children: items,
      } as DsNavigationItem,
    ];
  }
}
